import React, { useState } from 'react';
import { Heart, MessageCircle, Check, MapPin, Phone } from 'lucide-react';
import { chatSeeds } from '../data/mockData';
import DetailImageCarousel from '../components/DetailImageCarousel';
import ChatDrawer from '../components/ChatDrawer';
import MorphButton from '../components/MorphButton';
import { requireAuth } from '../components/AuthGate';
import {
  contactFor,
  waLinkForContact,
  telLinkForContact,
} from '../lib/mtfaContacts';

const AUTO_REPLIES = [
  "Thanks for asking — our team will follow up with the details.",
  "Noted. We'll get back to you shortly on this.",
  "Good question. Let us check and confirm with you soon.",
  "Appreciate you reaching out. We'll be in touch."
];

// Declared at module scope on purpose: defining it inside the page component
// would give React a new component type on every render, remounting the
// MorphButton and discarding the timer that fires its onComplete.
function ActionBar({
  className = '', isSaved, onToggleSave, messageCount, onOpenChat,
  ctaLabel, ctaSuccessLabel, onCta, onCtaComplete, done
}) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <button
        type="button"
        data-testid="detail-save-button"
        onClick={onToggleSave}
        aria-label="Save"
        className="w-11 h-11 rounded-full border border-border flex items-center justify-center flex-shrink-0"
      >
        <Heart className={`h-5 w-5 ${isSaved ? 'fill-rose-500 text-rose-500' : 'text-[#0F172A]'}`} />
      </button>
      <button
        type="button"
        data-testid="detail-chat-button"
        onClick={onOpenChat}
        className="h-11 px-4 rounded-full border border-border flex items-center gap-1.5 text-sm font-semibold text-[#0F172A] flex-shrink-0"
      >
        <MessageCircle className="h-4.5 w-4.5" />
        Chat{messageCount ? ` (${messageCount})` : ''}
      </button>
      <MorphButton
        data-testid="offering-cta-button"
        idleLabel={ctaLabel}
        successLabel={ctaSuccessLabel}
        onClick={onCta}
        onComplete={onCtaComplete}
        forceSuccess={done}
        className="flex-1 h-11 rounded-full font-bold text-sm bg-primary text-white hover:bg-primary-hover"
      />
    </div>
  );
}

// Service handoff row: WhatsApp + call the exact team that runs this service,
// using the verified MTFA number for that service (see lib/mtfaContacts.js).
// The same wa.me URL works on iOS/Android (deep-links WhatsApp) and desktop
// (opens WhatsApp Web) — we render as plain <a> so the browser picks the
// right handler natively.
function ContactRow({ item }) {
  if (!item?.id) return null;
  const contact = contactFor(item);
  if (!contact?.phoneE164) return null;
  const waHref = waLinkForContact(contact.key, { serviceName: item.title });
  const telHref = telLinkForContact(contact.key);
  return (
    <div
      className="flex flex-wrap items-center gap-2 mb-5 md:max-w-md"
      data-testid="offering-contact-row"
      data-contact-key={contact.key}
      data-wa-number={contact.phoneE164}
    >
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="offering-whatsapp-button"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
      >
        <MessageCircle className="h-4 w-4" />
        WhatsApp {contact.label}
      </a>
      <a
        href={telHref}
        data-testid="offering-call-button"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-semibold text-[#0F172A]"
      >
        <Phone className="h-4 w-4" />
        Call {contact.phoneDisplay}
      </a>
    </div>
  );
}

/**
 * Detail page shared by dialysis centres, casket packages and ways to give.
 * Mirrors VolunteerDetail's layout — sticky carousel on the left at desktop,
 * fixed action bar on mobile — but renders generic `facts` and `highlights`
 * blocks instead of the volunteer-specific sections.
 *
 * The CTA is gated: anyone can read the page, but committing to it requires an
 * account. `requireAuth` opens as an overlay so this page keeps its state.
 */
export default function OfferingDetail({
  item,
  savedIds,
  onToggleSave,
  onBack,
  ctaLabel = 'Continue',
  ctaSuccessLabel = 'Confirmed',
  onCtaComplete,
  chatSubtitle = 'IhsanOne Team',
  initialChatEntry = null,
  onBackToInbox
}) {
  const [messages, setMessages] = useState(() => chatSeeds[item.id] || []);
  const [draft, setDraft] = useState('');
  const [chatOpen, setChatOpen] = useState(initialChatEntry === 'inbox');
  const [chatEntryFrom, setChatEntryFrom] = useState(initialChatEntry === 'inbox' ? 'inbox' : 'card');
  const [done, setDone] = useState(false);

  const isSaved = savedIds?.has(item.id);
  const images = item.images?.length ? item.images : [item.image];

  const handleSend = () => {
    if (!draft.trim()) return;
    setMessages((prev) => [...prev, { id: Date.now(), sender: 'me', text: draft.trim(), time: 'Now' }]);
    setDraft('');
    setTimeout(() => {
      const reply = AUTO_REPLIES[Math.floor(Math.random() * AUTO_REPLIES.length)];
      setMessages((prev) => [...prev, { id: Date.now() + 1, sender: 'org', text: reply, time: 'Now' }]);
    }, 1200);
  };

  const handleChatBack = () => {
    setChatOpen(false);
    if (chatEntryFrom === 'inbox') onBackToInbox?.();
  };

  // Returning false keeps MorphButton idle, so a dismissed gate leaves no
  // misleading success state behind.
  const handleCta = async () => {
    const authed = await requireAuth();
    if (!authed) return false;
    setDone(true);
    return true;
  };

  const actionProps = {
    isSaved,
    onToggleSave: () => onToggleSave?.(item.id),
    messageCount: messages.length,
    onOpenChat: () => { setChatEntryFrom('card'); setChatOpen(true); },
    ctaLabel,
    ctaSuccessLabel,
    onCta: handleCta,
    onCtaComplete,
    done
  };

  return (
    <div className="min-h-screen bg-background page-fade-in flex flex-col md:block" data-testid="offering-detail-page">
      <div className="md:desktop-shell md:py-8 md:flex md:flex-row md:gap-10 md:items-start">
        <div className="md:w-[46%] md:flex-shrink-0 md:sticky md:top-6 md:h-[560px]">
          <DetailImageCarousel
            images={images}
            extra={item.carouselExtra}
            tag={item.tag}
            tagColor={item.tagColor}
            onBack={onBack}
          />
        </div>

        <div className="flex-1 px-4 md:px-0 pt-4 md:pt-0 pb-28 md:pb-10">
          <h1 className="text-xl md:text-2xl font-extrabold text-[#0F172A] leading-tight">{item.title}</h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            {item.org} &nbsp;·&nbsp; {item.date}
          </p>

          <ActionBar className="hidden md:flex mt-5 mb-2" {...actionProps} />

          {item.location && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground my-4">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{item.location}</span>
            </div>
          )}

          <p className="text-sm text-foreground leading-relaxed mb-5">{item.description}</p>

          <ContactRow item={item} />

          {item.facts?.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-6 md:max-w-md">
              {item.facts.map((f) => (
                <div key={f.label} className="rounded-xl bg-muted/60 p-3">
                  <p className="text-xs font-bold text-[#0F172A]">{f.label}</p>
                  <p className="text-sm font-extrabold text-primary mt-0.5">{f.value}</p>
                </div>
              ))}
            </div>
          )}

          {item.highlights?.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-[#0F172A] mb-2.5">What&apos;s included</h3>
              <ul className="flex flex-col gap-2.5">
                {item.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="leading-snug">{h}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      <ActionBar
        className="md:hidden fixed bottom-0 inset-x-0 z-[55] bg-white border-t border-border/60 px-3 py-2.5"
        {...actionProps}
      />

      <ChatDrawer
        isOpen={chatOpen}
        title={item.title}
        subtitle={chatSubtitle}
        messages={messages}
        draft={draft}
        onDraftChange={setDraft}
        onSend={handleSend}
        onBack={handleChatBack}
      />
    </div>
  );
}
